/**
 * Daily Challenges — the static daily challenge table + progress math.
 *
 * ONE source of truth consumed by three surfaces:
 *  - client widget (rendering: titles, icons, thresholds, gold)
 *  - main-server DailyChallengeService (progress application, claim
 *    validation, auto-bank at rollover)
 *  - analytics (challenge ids in event payloads)
 *
 * Design invariants (decided 2026-07-04, owner-approved):
 *  - STATIC set: same challenges every UTC day, no rotation document.
 *  - Progress is ONE cumulative daily counter per challenge; levels are
 *    thresholds on that counter. `claimed` = number of levels collected.
 *  - Levels pay CHALLENGE_LEVEL_GOLD (10/15/25). A challenge may define
 *    fewer than 3 levels (e.g. daily login = a single 10-gold level).
 *  - Gameplay counters are SERVER-COMPUTED on the game-server and ride the
 *    /internal/player-stats bridge — never client-reported (gold is premium
 *    and dice values are client-influenced). Multiplayer only; solo rooms
 *    publish no deltas.
 *  - Thresholds are volume-tuned for Phone-Dice-style play: level 1 lands in
 *    the first short session, level 3 needs a real day of rounds.
 */
/** Gold paid per completed level (level 1, 2, 3). */
export const CHALLENGE_LEVEL_GOLD = [10, 15, 25];
export const CHALLENGE_PAGE_TITLES = ['THE GRIND', 'THE STREETS', 'CEE-LO', 'CRAPS'];
export const DAILY_CHALLENGES = [
    // ── Page 0: THE GRIND (always completable, no table required) ──
    {
        id: 'grind_login',
        page: 0,
        icon: '🏠',
        title: 'Pull Up',
        desc: 'Show up on the block today',
        thresholds: [1],
    },
    {
        id: 'grind_bonuses',
        page: 0,
        icon: '🎁',
        title: 'Stack Ya Bonuses',
        desc: 'Collect daily bonuses and friend gifts',
        thresholds: [1, 2, 5],
    },
    {
        id: 'grind_solo',
        page: 0,
        icon: '💪',
        title: 'Run Ya Reps',
        desc: 'Play Solo Grind rounds',
        thresholds: [5, 20, 50],
    },
    // ── Page 1: THE STREETS (multiplayer, any game) ──
    {
        id: 'streets_rounds',
        page: 1,
        icon: '🎲',
        title: 'Roll Wit It',
        desc: 'Play rounds in any game',
        thresholds: [5, 15, 40],
    },
    {
        id: 'streets_wins',
        page: 1,
        icon: '💵',
        title: 'Get Paid',
        desc: 'Win bets: fades, bank or sidebets',
        thresholds: [3, 10, 25],
    },
    {
        id: 'streets_sidebets',
        page: 1,
        icon: '🎯',
        title: 'Side Hustle',
        desc: 'Place sidebets',
        thresholds: [3, 8, 20],
    },
    // ── Page 2: CEE-LO ──
    {
        id: 'ceelo_rounds',
        page: 2,
        icon: '🎲',
        title: 'Run the Block',
        desc: 'Play cee-lo rounds',
        thresholds: [3, 10, 25],
    },
    {
        id: 'ceelo_instant',
        page: 2,
        icon: '🔥',
        title: 'Head Crack',
        desc: 'Roll 4-5-6 or trips',
        thresholds: [1, 3, 8],
    },
    {
        id: 'ceelo_shooter',
        page: 2,
        icon: '👑',
        title: 'Beat the Bank',
        desc: 'Win as shooter against the bank',
        thresholds: [2, 6, 15],
    },
    // ── Page 3: CRAPS ──
    {
        id: 'craps_rounds',
        page: 3,
        icon: '🎲',
        title: 'Shoot Ya Shot',
        desc: 'Play craps rounds',
        thresholds: [3, 10, 25],
    },
    {
        id: 'craps_points',
        page: 3,
        icon: '🎯',
        title: 'Point Made',
        desc: 'Hit your point as shooter',
        thresholds: [1, 3, 7],
    },
    {
        id: 'craps_naturals',
        page: 3,
        icon: '⚡',
        title: 'Natural',
        desc: 'Roll 7 or 11 on the come out',
        thresholds: [1, 3, 6],
    },
];
/** Fast lookup by id. */
export const DAILY_CHALLENGES_BY_ID = new Map(DAILY_CHALLENGES.map((d) => [d.id, d]));
/** Levels currently completed by a cumulative counter (0..thresholds.length). */
export function challengeLevelForProgress(def, progress) {
    let level = 0;
    for (const t of def.thresholds) {
        if (progress >= t)
            level++;
        else
            break;
    }
    return level;
}
/** Gold owed for completed levels beyond what's already claimed. */
export function unclaimedGold(def, progress, claimed) {
    const done = challengeLevelForProgress(def, progress);
    let gold = 0;
    for (let lvl = claimed; lvl < done; lvl++) {
        gold += CHALLENGE_LEVEL_GOLD[Math.min(lvl, CHALLENGE_LEVEL_GOLD.length - 1)];
    }
    return gold;
}
/** Total gold a single challenge pays when fully claimed. */
export function challengeMaxGold(def) {
    let gold = 0;
    for (let lvl = 0; lvl < def.thresholds.length; lvl++) {
        gold += CHALLENGE_LEVEL_GOLD[Math.min(lvl, CHALLENGE_LEVEL_GOLD.length - 1)];
    }
    return gold;
}
//# sourceMappingURL=DailyChallenges.js.map